import React, { useState, useEffect, useRef } from 'react';
import {
  Button, Col, Modal, Row, Typography,
  Form, Input, Select,
  message, Space,
  Spin
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import useForm from '../hooks/useForm';
import encryptStorage from '../util/encryption';
import { COUNTRY_STATE_DATA } from '../util/country_state';

import { EyeOutlined, EyeInvisibleOutlined, CreditCardOutlined, CopyOutlined } from '@ant-design/icons';
import { collectionsAxios } from '../util/Api';
import { Environments } from '../constants/System';

const { Text } = Typography;
// Configuration
const config = {
  redirect_url: window.location.origin + window.location.pathname,
  public_key: process.env.REACT_APP_ENVIRONMENT === Environments.PRODUCTION
    ? `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAuUUAlSnbIaEBxSCa4lwM
ePr7MSEzvSDR/S0xDGKRRzHYEZCglYlDyXDhm++Q2MhuAyO468IgTLrf4gFVpu7I
CL/ZKyA1FWk5ezDdlmLRyKbJ/z4u4CVg+Fs4rjg1DSqfgaqDGbtcjLAc8qUN80/2
AV4UQU9DZOKIFloPhGnD+82W8TOe01PJpHMPoQctcjuPDMb2i+xtX7VzObPXLpS5
XXQE+rOonXcnY8JHl0qN8OxwwSE6YETZYtS0ddzQXix7TeW/YkQ32nlRosSmJp3L
44aOwOggUOtLfPl0TPh7ucxV6wszhH80QiCDRh+nG2E4Q8mEHBOopKRR9WeKZ1C8
WwIDAQAB
-----END PUBLIC KEY-----`
    : `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA0AgKKOgLFzPDX4BlXYaJ
5OUDAEXpViiKUKGWjBdwwScLmPjW0QtKHsEJ8iZEFjfPblQbH8CNoJBdw2Y3U53R
ZOM8t/fR8rmPO4igov156cHOX5NsywMVuRY61tdyXkWuWHPbn3lmSvYyfXiSUOAW
KCUkO7im3lOFbGKEiE/x6ZLnjCv5eE6k+Vm5ncJPDT7HI4mtEeOMorEuZpUzX26h
59KmUxAOwkfGBJz4ISG3HluFVClR2HlhS7CCw+W0CIE+eiHG+Z0XA7Ks0jXeVfzF
AuX/A3vc9YKWzuFNLyqZbgxqzHD+7k99NUbiszbddzoYPOaNa0qk/6bpWjyCrqoz
VQIDAQAB
-----END PUBLIC KEY-----`,
};

const GeneratePRN = ({ merchantId }) => {
  const dispatch = useDispatch();
  const token = encryptStorage.getItem('token') || '';

  const [generatePRNModal, setGeneratePRNModal] = useState(false);
  const [generatePRNError, setGeneratePRNError] = useState(null);

  const openGeneratePRN = () => {
    setGeneratePRNModal(true);
  };

  const handleCancel = () => {
    setGeneratePRNModal(false);
  };


  const {
    form
  } = useForm();

  const [loading, setLoading] = useState(false);

  // Generate unique merchant reference
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  };
  const merchant_reference = generateUUID();

  // Handle form submission
  const onOk = async () => {
    const values = form.current.getFieldsValue();

    setLoading(true);
    try {

      const paymentData = {
        currency: values.currency,
        amount: values.amount,
        merchant_id: merchantId,
        merchant_reference,
        narration: values.narration || ''
      };

      const response = await fetch(`${collectionsAxios.defaults.baseURL}/hp-prn-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${token}`,
        },
        body: JSON.stringify(paymentData),
      });
      const data = await response.json();
      if (!response.ok || !data.data) {
        throw new Error(data.message || 'Payment initiation failed');
      }

      const kitegateway_reference = data.data.kitegateway_reference;
      message.info({
        content: (
          <div>
            <p>Please copy Payment Reference Number (PRN) </p>
            <Space style={{ width: '100%' }}>
              <Input value={kitegateway_reference} readOnly style={{ width: 400 }} />
              <Button
                type='default'
                className="ant-btn-md ant-btn-background-ghost"
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.write(kitegateway_reference)
                    .then(() => message.success('Link copied to clipboard!'))
                    .catch(() => {
                      // Fallback to document.execCommand
                      try {
                        const textArea = document.createElement('textarea');
                        textArea.value = kitegateway_reference;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        console.log('execCommand: Link copied successfully'); // Debug log
                        message.success('Link copied to clipboard!');
                      } catch (fallbackErr) {
                        console.error('execCommand failed:', fallbackErr); // Debug log
                        message.error('Failed to copy link. Please select and copy the URL manually.');
                      }
                    });
                }}
              >
                Copy
              </Button>
            </Space>
          </div>
        ),
        duration: 0,
        style: { maxWidth: 600, margin: '0 auto' },
      });
      setLoading(false);
      setGeneratePRNModal(false);

    } catch (error) {
      setLoading(false);
      message.error('Error: ' + error.message);
    }
  };



  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  return (
    <>
      <Row>
        <Col xl={24} lg={24} md={24} sm={24} xs={24}>
          <Button style={{ float: 'left' }}
            type="primary"
            className="ant-btn-md"
            title="Create Transaction" icon={<CreditCardOutlined />}
            onClick={openGeneratePRN}>
            Generate PRN
          </Button>
        </Col>
      </Row>

      <Modal
        title="Create Payment Reference Number (PRN)"
        open={generatePRNModal}
        onOk={onOk}
        okText={'Create PRN'}
        onCancel={handleCancel}
      >
        {
          generatePRNError && <Text type="danger">{generatePRNError}</Text>
        }
        <Form
          name="create-transaction-form"
          ref={form}
          scrollToFirstError
          className="gx-form-row0"
          {...formItemLayout}
        >
          <Row>
            <Col xl={24} lg={24} md={24} sm={24} xs={24}>
              <Form.Item
                label="Amount"
                name="amount"
                rules={[
                  { required: true, message: 'Please enter a valid amount' },
                  {
                    pattern: new RegExp(/^[0-9]+$/),
                    message: 'Amount must be greater than 0'
                  },
                ]}
              >
                <Input type="number" step="0.01" placeholder="Enter amount" />
              </Form.Item>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: 'Please select a currency' }]}
              >
                <Select placeholder="Select currency">
                  {['USD', 'EUR', 'GBP', 'CAD', 'AUD'].map(currency => (
                    <Select.Option key={currency} value={currency}>{currency}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Narration"
                name="narration"
              >
                <Input.TextArea placeholder="Describe what you are paying for" rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {loading && (
          <Spin className='gx-text-center' />
        )}
      </Modal>

    </>
  );
};

export default GeneratePRN;
